import { Injectable, inject } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { tap, finalize } from 'rxjs/operators';

import { HttpMethodEnum } from '../../models/enums/http-method.enum';
import { ErrorMessagesService } from '../error-messages.service';
import { ErrorMessageModel } from '../../core/models/error-message.model';
import { ErrorMessageTypeEnum } from '../../core/models/enums/error-message-type.enum';
import { AlertService } from '../alert.service';
import { SecurityStorage } from '../storages/security.storage';
import { FailedRequestsRepository } from '../repositories/failed-requests.repository';

@Injectable()
export class ErrorHandlerHttpInterceptor implements HttpInterceptor {
    private router = inject(Router);
    private failedRequestsRepository = inject(FailedRequestsRepository);
    private errorMessagesService = inject(ErrorMessagesService);
    private alertService = inject(AlertService);
    private securityStorage = inject(SecurityStorage);

    private pendingTransactions = 0;

    intercept(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        let serverError: any;
        let hasError = false;

        if (this.pendingTransactions === 0) {
            this.errorMessagesService.setError(undefined);
        }

        this.pendingTransactions++;

        return next.handle(request).pipe(
            tap(
                event => {},
                error => {
                    hasError = true;
                    serverError = error;
                }
            ),
            finalize(() => {
                if (hasError && serverError instanceof HttpErrorResponse) {
                    switch (serverError.status) {
                        case 0: // Handle offline mode
                            const req = request.clone();

                            if (
                                req.method === HttpMethodEnum.Post ||
                                req.method === HttpMethodEnum.Put ||
                                req.method === HttpMethodEnum.Delete
                            ) {
                                this.failedRequestsRepository.saveRequest(
                                    req.urlWithParams,
                                    req.method,
                                    req.body
                                );
                            }
                            break;
                        case 401: // Handle user not authenticated or forbidden
                        case 403:
                            this.securityStorage.eraseUserInfo();
                            this.router.navigate(['login']);
                            break;
                        case 400: // Handle server exceptions
                            this.errorMessagesService.setError(
                                new ErrorMessageModel({
                                    message: serverError.error.message || serverError.error.detail,
                                    type: ErrorMessageTypeEnum.Warning
                                })
                            );

                            if (
                                this.errorMessagesService
                                    .shouldDisplayAlertOnError
                            ) {
                                this.alertService.showError(
                                    serverError.error.message || serverError.error.detail,
                                    undefined
                                );
                            }
                            break;
                        case 500:
                            this.errorMessagesService.setError(
                                new ErrorMessageModel({
                                    message: serverError.error.message,
                                    type: ErrorMessageTypeEnum.Warning
                                })
                            );

                            if (
                                this.errorMessagesService
                                    .shouldDisplayAlertOnError
                            ) {
                                this.alertService.showError(
                                    serverError.error.message,
                                    undefined
                                );
                            }
                            break;
                        case 588: // Handle business exceptions
                            this.errorMessagesService.setError(
                                new ErrorMessageModel({
                                    message: serverError.error.message,
                                    type: ErrorMessageTypeEnum.Warning
                                })
                            );

                            if (
                                this.errorMessagesService
                                    .shouldDisplayAlertOnError
                            ) {
                                this.alertService.showAlert(
                                    serverError.error.message,
                                    undefined
                                );
                            }
                            break;
                    }
                }
            })
        );
    }
}
